/** MPC unlock: account-level payment password (login session) + per-wallet decrypted session JSON. */

import {
  decryptAccountPayUnlock,
  encryptAccountPayUnlock,
  type AccountPayUnlockPayload,
} from './sessionPayPasswordCrypto';
import { getSessionToken, onSessionTokenChange } from './sessionContext';

export type MpcUnlockEntry = {
  sessionJson: string;
  payPwdWire: string;
};

type AccountPayUnlock = AccountPayUnlockPayload;

type StoredAccountUnlock = {
  v: 1;
  sessionToken: string;
  ciphertext: string;
};

const ACCOUNT_UNLOCK_KEY = 'cointrader_account_pay_unlock';
const WALLET_SESSION_PREFIX = 'cointrader_mpc_wallet_session:';

let accountPayUnlock: AccountPayUnlock | null = null;
let accountUnlockLoadPromise: Promise<void> | null = null;
const walletSessionJson = new Map<number, string>();

function isLegacyPlainUnlock(raw: string): boolean {
  try {
    const parsed = JSON.parse(raw) as { payPwdPlain?: string };
    return typeof parsed.payPwdPlain === 'string' && parsed.payPwdPlain.length > 0;
  } catch {
    return false;
  }
}

function parseStoredAccountUnlock(raw: string): StoredAccountUnlock | null {
  try {
    const parsed = JSON.parse(raw) as Partial<StoredAccountUnlock>;
    const sessionToken = parsed.sessionToken?.trim() ?? '';
    const ciphertext = parsed.ciphertext?.trim() ?? '';
    if (parsed.v !== 1 || !sessionToken || !ciphertext) {
      return null;
    }
    return { v: 1, sessionToken, ciphertext };
  } catch {
    return null;
  }
}

async function loadAccountUnlockFromStorage(sessionToken: string): Promise<AccountPayUnlock | null> {
  try {
    const raw = localStorage.getItem(ACCOUNT_UNLOCK_KEY);
    if (!raw?.trim()) {
      return null;
    }
    if (isLegacyPlainUnlock(raw)) {
      localStorage.removeItem(ACCOUNT_UNLOCK_KEY);
      return null;
    }
    const stored = parseStoredAccountUnlock(raw);
    if (!stored || stored.sessionToken !== sessionToken) {
      return null;
    }
    return await decryptAccountPayUnlock(sessionToken, stored.ciphertext);
  } catch {
    return null;
  }
}

async function saveAccountUnlockToStorage(unlock: AccountPayUnlock | null, sessionToken: string): Promise<void> {
  if (!unlock || !sessionToken) {
    localStorage.removeItem(ACCOUNT_UNLOCK_KEY);
    return;
  }
  const ciphertext = await encryptAccountPayUnlock(sessionToken, unlock);
  const stored: StoredAccountUnlock = { v: 1, sessionToken, ciphertext };
  localStorage.setItem(ACCOUNT_UNLOCK_KEY, JSON.stringify(stored));
}

function loadWalletSessionFromStorage(walletId: number): string {
  const raw = localStorage.getItem(`${WALLET_SESSION_PREFIX}${walletId}`);
  return raw?.trim() ?? '';
}

function saveWalletSessionToStorage(walletId: number, sessionJson: string): void {
  const key = `${WALLET_SESSION_PREFIX}${walletId}`;
  if (sessionJson.trim()) {
    localStorage.setItem(key, sessionJson);
  } else {
    localStorage.removeItem(key);
  }
}

function clearAllWalletSessionsFromStorage(): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(WALLET_SESSION_PREFIX)) {
      keys.push(k);
    }
  }
  for (const k of keys) {
    localStorage.removeItem(k);
  }
}

function notifyUnlockStateChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('cointrader-mpc-unlock-changed'));
  }
}

async function reloadAccountUnlockFromSession(): Promise<void> {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    accountPayUnlock = null;
    notifyUnlockStateChanged();
    return;
  }
  accountPayUnlock = await loadAccountUnlockFromStorage(sessionToken);
  if (!accountPayUnlock) {
    walletSessionJson.clear();
  }
  notifyUnlockStateChanged();
}

export async function ensureAccountUnlockLoaded(): Promise<void> {
  if (accountPayUnlock) {
    return;
  }
  if (!accountUnlockLoadPromise) {
    accountUnlockLoadPromise = reloadAccountUnlockFromSession().finally(() => {
      accountUnlockLoadPromise = null;
    });
  }
  await accountUnlockLoadPromise;
}

function walletSessionFor(walletId: number): string {
  const mem = walletSessionJson.get(walletId);
  if (mem?.trim()) {
    return mem;
  }
  const stored = loadWalletSessionFromStorage(walletId);
  if (stored) {
    walletSessionJson.set(walletId, stored);
  }
  return stored;
}

/** Cache account payment password (login session) and optional per-wallet session JSON. */
export async function setMpcUnlock(
  walletId: number,
  entry: MpcUnlockEntry,
  payPwdPlain?: string,
): Promise<void> {
  const sessionToken = getSessionToken();
  const plain = payPwdPlain?.trim();
  if (plain) {
    accountPayUnlock = { payPwdPlain: plain, payPwdWire: entry.payPwdWire };
    if (sessionToken) {
      await saveAccountUnlockToStorage(accountPayUnlock, sessionToken);
    }
  } else if (accountPayUnlock) {
    accountPayUnlock.payPwdWire = entry.payPwdWire;
    if (sessionToken) {
      await saveAccountUnlockToStorage(accountPayUnlock, sessionToken);
    }
  } else if (entry.payPwdWire) {
    return;
  }
  if (walletId > 0 && entry.sessionJson.trim()) {
    walletSessionJson.set(walletId, entry.sessionJson);
    saveWalletSessionToStorage(walletId, entry.sessionJson);
  }
  notifyUnlockStateChanged();
}

export async function getAccountPayPasswordPlain(): Promise<string | undefined> {
  await ensureAccountUnlockLoaded();
  return accountPayUnlock?.payPwdPlain;
}

export async function getMpcUnlock(walletId: number): Promise<MpcUnlockEntry | undefined> {
  await ensureAccountUnlockLoaded();
  if (!accountPayUnlock) {
    return undefined;
  }
  return {
    sessionJson: walletSessionFor(walletId),
    payPwdWire: accountPayUnlock.payPwdWire,
  };
}

/** True when payment password was verified in the current login session (any wallet). */
export async function isWalletSessionUnlocked(_walletId?: number): Promise<boolean> {
  await ensureAccountUnlockLoaded();
  return accountPayUnlock !== null;
}

/** Clear all unlock state, or only one wallet's cached session JSON. */
export function clearMpcUnlockCache(walletId?: number): void {
  if (walletId === undefined) {
    accountPayUnlock = null;
    walletSessionJson.clear();
    localStorage.removeItem(ACCOUNT_UNLOCK_KEY);
    clearAllWalletSessionsFromStorage();
    notifyUnlockStateChanged();
    return;
  }
  walletSessionJson.delete(walletId);
  saveWalletSessionToStorage(walletId, '');
}

if (typeof window !== 'undefined') {
  onSessionTokenChange(() => {
    void reloadAccountUnlockFromSession();
  });

  window.addEventListener('storage', (ev) => {
    if (ev.key === ACCOUNT_UNLOCK_KEY) {
      void reloadAccountUnlockFromSession();
      return;
    }
    if (ev.key?.startsWith(WALLET_SESSION_PREFIX)) {
      const wid = Number(ev.key.slice(WALLET_SESSION_PREFIX.length));
      if (!Number.isFinite(wid) || wid <= 0) {
        return;
      }
      if (ev.newValue?.trim()) {
        walletSessionJson.set(wid, ev.newValue);
      } else {
        walletSessionJson.delete(wid);
      }
    }
  });
}
